import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useScopedTranslation from 'hooks/useScopedTranslation';
import { noClientsDemoI18n } from '../i18n';
import {
  getNoClientsEmailThread,
  getNoClientsWhatsappThread,
} from 'services/noClientsDemoService';
import EmailThread from './EmailThread';
import WhatsappThread from './WhatsappThread';
import './clientDetail.scss';

// Vista aparte del hilo de un canal (WhatsApp o Correo) de un cierre. Se llega desde el
// botón "Ver" de la tabla de contactos del detalle del cliente. Ruta: .../contacto/:canal.
const ContactThreadView = ({ DesignSystemComponents }) => {
  const { Card, Button } = DesignSystemComponents || {};
  const navigate = useNavigate();
  const { dealId, canal, ref } = useParams();
  const { t } = useScopedTranslation(noClientsDemoI18n);

  const isWhatsapp = canal === 'whatsapp';

  const [items, setItems] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      if (!dealId) {
        setError(t('nc_cd_error'));
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const data = isWhatsapp
          ? await getNoClientsWhatsappThread(dealId, controller.signal)
          : await getNoClientsEmailThread(dealId, controller.signal);
        setItems(Array.isArray(data) ? data : []);
      } catch (e) {
        if (e.name !== 'AbortError' && e.name !== 'CanceledError') {
          console.error('Error cargando hilo de contacto:', e);
          setError(t('nc_cd_error'));
          setItems([]);
        }
      } finally {
        setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [dealId, isWhatsapp, t]);

  // Filtra al correo puntual (por HubspotEmailId) o a la conversación puntual (por ThreadId).
  const shown = useMemo(() => {
    const list = Array.isArray(items) ? items : [];
    if (!ref) return list;
    return isWhatsapp
      ? list.filter((m) => (m.threadId || '_') === ref)
      : list.filter((e) => e.hubspotEmailId === ref);
  }, [items, ref, isWhatsapp]);

  if (!Card) return null;

  const title = isWhatsapp
    ? t('nc_cd_whatsappThreadTitle')
    : t('nc_cd_emailThreadTitle');

  return (
    <div className='no-clients-page client-detail-page'>
      <div className='d-flex align-items-center gap-2 mb-2'>
        {Button ? (
          <Button
            variant='naked'
            iconStart='chevronLeft'
            label={t('nc_back')}
            size='sm'
            onClick={() => navigate(-1)}
          />
        ) : (
          <button type='button' onClick={() => navigate(-1)}>
            {t('nc_back')}
          </button>
        )}
      </div>

      <div className='mb-4'>
        <h2 className='fw-semibold'>{title}</h2>
        <h4 className='fw-normal'>ID: {dealId}</h4>
      </div>

      <Card specialPadding='px-4 pt-4 pb-4'>
        {loading && (
          <p className='caption color-gray-600 mb-0'>
            {t('nc_cd_threadLoading')}
          </p>
        )}
        {error && !loading && <p className='text-danger mb-0'>{error}</p>}
        {!loading && !error && (shown?.length ?? 0) === 0 && (
          <p className='caption color-gray-600 mb-0'>
            {t('nc_cd_contactsEmpty')}
          </p>
        )}
        {!loading &&
          !error &&
          (shown?.length ?? 0) > 0 &&
          (isWhatsapp ? (
            <WhatsappThread messages={shown} />
          ) : (
            <EmailThread emails={shown} />
          ))}
      </Card>
    </div>
  );
};

export default ContactThreadView;
